import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/cases';

const navItems = [
  { label: '首页', to: '/' },
  { label: '爆料列表', to: '/cases' },
  { label: '关于我们', to: '/about' },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/cases?q=${encodeURIComponent(q)}` : '/cases');
    setKeyword('');
  }

  function isActive(to: string) {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  }

  return (
    <>
      <div className="hidden bg-foreground-950 text-background-100 md:block">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 py-2 text-xs">
          <p className="flex items-center gap-2">
            <i className="ri-shield-check-line text-accent-400"></i>
            <span>实锤为本 · 有图有真相 · 材料均标注核实状态</span>
          </p>
          <div className="flex items-center gap-5">
            <a href="mailto:contact@baoliao.example.com" className="hover:text-accent-300">
              contact@baoliao.example.com
            </a>
            <Link to="/about#submit" className="hover:text-accent-300">
              投稿爆料
            </Link>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1280px] items-center gap-6 px-4 py-3 md:px-6 md:py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600 text-background-50">
              <i className="ri-alarm-warning-line text-xl"></i>
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-lg font-bold text-foreground-950">网红爆料网</span>
              <span className="block text-[11px] tracking-wide text-foreground-500">实锤证据 · 有图有真相</span>
            </span>
          </Link>

          <nav className="ml-2 hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive(item.to)
                    ? 'bg-primary-100 font-medium text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                aria-expanded={catOpen}
              >
                主题分类
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 w-64 rounded-lg border border-background-200 bg-background-50 p-2 shadow-sm">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/cases?cat=${cat.id}`}
                      className="flex items-start gap-3 rounded-md px-3 py-2 hover:bg-background-100"
                    >
                      <i className={`${cat.icon} mt-0.5 text-primary-500`}></i>
                      <span>
                        <span className="block text-sm font-medium text-foreground-900">{cat.name}</span>
                        <span className="block text-xs text-foreground-500">{cat.desc.slice(0, 18)}…</span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <form onSubmit={handleSearch} className="ml-auto hidden items-center md:flex">
            <div className="flex items-center rounded-md border border-background-300 bg-background-100 px-3 focus-within:border-primary-400">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索爆料关键词"
                aria-label="搜索爆料关键词"
                className="w-32 bg-transparent px-2 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-44"
              />
            </div>
          </form>

          <Link
            to="/about#submit"
            className="hidden whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 lg:block"
          >
            我要投稿
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <div className="mx-auto max-w-[1280px] px-4 py-4 md:px-6">
              <form onSubmit={handleSearch} className="mb-3 flex items-center rounded-md border border-background-300 bg-background-100 px-3">
                <i className="ri-search-line text-sm text-foreground-500"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索爆料关键词"
                  aria-label="搜索爆料关键词"
                  className="w-full bg-transparent px-2 py-2 text-sm outline-none"
                />
              </form>
              <nav className="flex flex-col" aria-label="移动端导航">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`rounded-md px-3 py-2.5 text-sm ${
                      isActive(item.to) ? 'bg-primary-100 text-primary-700' : 'text-foreground-700'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                <p className="mt-3 px-3 text-xs font-medium text-foreground-500">主题分类</p>
                <div className="mt-1 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/cases?cat=${cat.id}`}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100"
                    >
                      <i className={`${cat.icon} text-primary-500`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/about#submit"
                  className="mt-3 rounded-md bg-primary-600 px-4 py-2.5 text-center text-sm font-medium text-background-50"
                >
                  我要投稿
                </Link>
              </nav>
            </div>
          </div>
        )}
      </header>
    </>
  );
}